import { fetch } from 'owa-fetch';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste
 *	> 'lodash-es/find' import is restricted from being used. lodash-es/find increases bundle size. Use native function instead. */
import find from 'lodash-es/find';

import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import type { TraceErrorObject } from 'owa-trace';

import {
    ORGANIZER_CHECK_ISPUBLISHED_ENTRYPOINT,
    ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES,
    ORGANIZER_GET_MAILBOXHASH_ENTRYPOINT,
    ORGANIZER_MASTER_PUBLISHING_CHECK_ENTRYPOINT,
} from '../store/schema/LinkEntryPoint';
import formatBookingMailboxGuid from '../utils/formatBookingMailboxGuid';
import { parseMeetingTypeDescription } from '../utils/parseServiceDescription';
import type { BookingService } from './schema/BookingService';
import { BookingPrivacyType } from './schema/MeetingTypeDescription';
import type { PersonalBookingData } from './schema/PersonalBookingData';

const pageDataPayloadRegex =
    /<script id=\"pagedatapayload\" type=\"text\/javascript\">([\S\s]+?)<\/script>/i;

/**
 * Function to determine if there should be a log reported based on count.
 * This will happen one of:
 * 1. We are at the limit for max tries.
 * 2. We are at a clean multiple of the max tries squared.
 */
function shouldReportBasedOnCount(count: number) {
    return (
        count >= ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES &&
        (count == ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES ||
            count % (ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES * ORGANIZER_CHECK_ISPUBLISHED_MAXTRIES) ==
                0)
    );
}

export async function getBookingSessionData(
    bookingMailbox: string,
    entryPoint: string,
    checkIsPublishedCount: number,
    bookingServiceId?: string,
    bookingId?: string
): Promise<PersonalBookingData | null> {
    // Added Eventify=1 to URL query for substrate to identify the client and go through the PBP authenticated path,
    // when session is not anonymous: cookie with anchor mailbox, user has PUId, etc.
    // If want to force the not authenticated path (fallback to email code verification) even when session is authenticated can be done by sending w/o eventify=1.
    // For a specific bookingServiceId, we don't use pbp/s/${bookingServiceId} because: 1) /pbp/s/${bookingServiceId} returns all services, 2) ?eventify=1 not yet supported.
    let rootHtml;
    const perfDatapoint = new PerformanceDatapoint('getBookingSessionData');
    try {
        if (bookingId) {
            rootHtml = await fetch(
                `/owa/calendar/${bookingMailbox}/pbp/${bookingId}?eventify=1`
            ).then(result => result.text());
        } else {
            rootHtml = await fetch(`/owa/calendar/${bookingMailbox}/pbp/?eventify=1`).then(result =>
                result.text()
            );
        }
    } catch (error) {
        const bookingPageError: TraceErrorObject = new Error('No bookingPage by organizer');
        error.additionalInfo = { error };
        perfDatapoint.endWithError(DatapointStatus.ServerError, bookingPageError);
        return null;
    }

    const pageDataPayloadMatch = pageDataPayloadRegex.exec(rootHtml);

    if (!pageDataPayloadMatch) {
        /*
         * Payload not returned is not considered as an error: when the call is from organizer's checkBookingPageIsPublished or getMailboxHashFromPayload,
         * and the number of checkIsPublishedTries is in a state where we want to report, while validating IsPublished replication completed in AD,
         *  to avoid false positive escalations.
         */
        if (
            (entryPoint != ORGANIZER_CHECK_ISPUBLISHED_ENTRYPOINT &&
                entryPoint != ORGANIZER_GET_MAILBOXHASH_ENTRYPOINT &&
                entryPoint != ORGANIZER_MASTER_PUBLISHING_CHECK_ENTRYPOINT) ||
            shouldReportBasedOnCount(checkIsPublishedCount)
        ) {
            // Log the Data point even only when error is real.
            perfDatapoint.addCustomData({
                checkIsPublishedCount,
                entryPoint,
                bookingServiceId,
                bookingId,
            });
            /*
             * Log pageDataPayload errors for an empty response if either:
             * 1) when call is from organizer and the count of retries is reaches max.
             * 2) call is from Attendee pages,there maybe some but unlikely, the attendee gets the shared link once IsPublished true verified as replicated.
             * Only scenario of such error is when the organizer un published, and attendee has an old shared link, hard to diag,
             * Need future work to distinguish the scenario, is user error anyway.
             */
            // Adding custom error to the datapoint information
            const getSessionError: TraceErrorObject = new Error(
                'No pageDataPayloadMatch by organizer'
            );
            perfDatapoint.endWithError(DatapointStatus.ServerError, getSessionError);
        } else {
            // Invalidate the datapoint so it doesn't get reported anywhere
            perfDatapoint.invalidate();
        }
        return null;
    }

    const pageData = JSON.parse(
        pageDataPayloadMatch[1].trim().replace('var PageDataPayload = ', '').replace('};', '}')
    );

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7006 (48,70): Parameter 'service' implicitly has an 'any' type.
    // @ts-expect-error
    let displayedServices: BookingService[] = pageData.Services?.map(service => ({
        Id: service.Id,
        Description: service.Description,
        bookwithmeDurationMinutes: service.bookwithmeDurationMinutes,
        FormattedDuration: service.FormattedDuration,
        IsDefaultLocationOnline: service.IsDefaultLocationOnline,
        IsMultiCustomerBooking: service.IsMultiCustomerBooking,
        Name: service.Name,
        SchedulingPolicy: service.SchedulingPolicy,
        PreBuffer: service.PreBufferInMinutes,
        PostBuffer: service.PostBufferInMinutes,
    }));

    // if the user never set buffers it will be undefined so need to set to zero in each service
    displayedServices.forEach(service => {
        service.PreBuffer = service.PreBuffer ?? 0;
        service.PostBuffer = service.PostBuffer ?? 0;
    });

    // If bookingId is present, only display the service related to the bookingId
    if (bookingId && pageData.Booking?.ServiceId) {
        bookingServiceId = pageData.Booking?.ServiceId;
    }

    if (displayedServices) {
        if (bookingServiceId) {
            // Need to filter bookingServiceId as the PBP service returns all services even when using "/pbp/s/${bookingServiceId}".
            const displayedService = find(
                displayedServices,
                service => service.Id === bookingServiceId
            );
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (74,34): Type 'BookingService | undefined' is not assignable to type 'BookingService'.
            // @ts-expect-error
            displayedServices = [displayedService];
        } else {
            // For the public booking page, only show public meeting types.
            const publicServices: BookingService[] = [];
            /* eslint-disable-next-line owa-custom-rules/forbid-foreach-with-variables-outside-of-function-scope -- (https://aka.ms/OWALintWiki)
             * https://dev.azure.com/outlookweb/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9650/Use-for-const-loop-of-instead-of-forEach
             *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead */
            displayedServices.forEach(service => {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (82,72): Argument of type 'string | null' is not assignable to parameter of type 'string'.
                // @ts-expect-error
                const serviceDescription = parseMeetingTypeDescription(service.Description);
                if (serviceDescription.privacyType === BookingPrivacyType.PublicMeetingType) {
                    publicServices.push(service);
                }
                displayedServices = publicServices;
            });
        }
    }

    // In PBP Id of the Self staff is always the same to signal use the organizer as unique staff member, in WellKnownStaff.cs:
    // SelfId = new Guid("1c1c7887-e0fc-479b-ac7d-6983d0f026ff");
    // So use the constant guid encoded to signal staff is organizer, needed to avoid regression introduced in substrate not returning the staffMembers.
    const selfIdEncoded = 'h3gcHPzgm0esfWmD0PAm/w==';

    // Need to support both @ in the address and the encoded value %40 that sometimes is provided.
    const organizerMailboxSplit =
        bookingMailbox.indexOf('@') > -1 ? bookingMailbox.split('@') : bookingMailbox.split('%40');
    const organizerGuid = organizerMailboxSplit[0];

    const response: PersonalBookingData = {
        Booking: pageData.Booking,
        BookingMailboxAddress:
            bookingMailbox /* instead of the pagedata payload SMTP mailbox address, save the obscured address required by privacy, obtained from attendee URL */,
        BookingMailboxHash: pageData.BookingMailboxHash,
        BookingMailboxPuid: pageData.BookingMailboxPuid,
        BusinessInfo: {
            Name: pageData.BusinessInfo?.Name,
        },
        BusinessLogo: pageData.BusinessLogo,
        BusinessTimeZone: pageData.BusinessTimeZone,
        BusinessTimeZoneName: pageData.BusinessTimeZoneName,
        StaffMembers: [{ Id: selfIdEncoded, Name: pageData.BusinessInfo?.Name }],
        Services: displayedServices,
        Settings: pageData.Settings,
        IsReadRequest: pageData.IsReadRequest,
        LoggedInUserHashedEmail: pageData.LoggedInUserHashedEmail,
        UserClientId: pageData.UserClientId,
    };
    perfDatapoint.addCustomData({
        bookingId,
        entryPoint: entryPoint ?? '',
        bookingMailboxGuid: formatBookingMailboxGuid(organizerGuid),
        bookingMailboxPuid: pageData.BookingMailboxPuid,
        loggedInUserHashedEmail: pageData.LoggedInUserHashedEmail,
        userClientId: pageData.UserClientId,
    });
    perfDatapoint.end();
    return response;
}
